// FILE: src/pages/Notifications/NotificationsPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { notificationsAPI } from '../../api/services';
import { toast } from 'react-toastify';
import '../../components/UI/UI.css';
import './Notifications.css';

const fmtDT = (d) => d ? new Date(d).toLocaleString('en-US',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'}) : '—';

const TYPE_CONFIG = {
  MedicaidAlert:        { icon:'🏥', color:'var(--danger)',  bg:'#fef2f2', label:'Medicaid Alert' },
  TrainingAlert:        { icon:'📚', color:'var(--warning)', bg:'#fffbeb', label:'Training Alert' },
  ProfileUpdateRequest: { icon:'👤', color:'var(--info)',    bg:'#eff6ff', label:'Profile Update' },
  VisitAlert:           { icon:'📅', color:'var(--primary)', bg:'var(--primary-pale)', label:'Visit Alert' },
  BillingAlert:         { icon:'💰', color:'var(--success)', bg:'var(--success-pale)', label:'Billing Alert' },
  default:              { icon:'🔔', color:'var(--gray-600)',bg:'var(--gray-50)',        label:'Notification' },
};

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [filtered,      setFiltered]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [search,        setSearch]        = useState('');
  const [tabFilt,       setTabFilt]       = useState('all');
  const [markAllLoad,   setMarkAllLoad]   = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await notificationsAPI.getMine();
      setNotifications(res.data.data || []);
      setFiltered(res.data.data || []);
    } catch { toast.error('Failed to load notifications.'); }
    finally   { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let data = [...notifications];
    if (tabFilt === 'unread') data = data.filter(n => !n.IsRead);
    if (tabFilt === 'read')   data = data.filter(n =>  n.IsRead);
    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter(n => (n.Message||'').toLowerCase().includes(q) || (n.NotificationType||'').toLowerCase().includes(q));
    }
    setFiltered(data);
  }, [search, tabFilt, notifications]);

  const handleMarkRead = async (id) => {
    try {
      await notificationsAPI.markRead(id);
      setNotifications(p => p.map(n => n.ID===id ? {...n, IsRead:1} : n));
    } catch { toast.error('Failed to mark as read.'); }
  };

  const handleMarkAllRead = async () => {
    setMarkAllLoad(true);
    try {
      await notificationsAPI.markAllRead();
      setNotifications(p => p.map(n => ({...n, IsRead:1})));
      toast.success('All notifications marked as read.');
    } catch { toast.error('Failed.'); }
    finally   { setMarkAllLoad(false); }
  };

  const handleDelete = async (id) => {
    try {
      await notificationsAPI.delete(id);
      setNotifications(p => p.filter(n => n.ID !== id));
      toast.success('Notification removed.');
    } catch { toast.error('Delete failed.'); }
  };

  const unreadCount = notifications.filter(n => !n.IsRead).length;

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1>Notifications</h1>
          <p>{unreadCount} unread · {notifications.length} total</p>
        </div>
        <div style={{display:'flex',gap:10}}>
          <div className="search-bar">
            <span className="search-icon">🔍</span>
            <input className="form-control" placeholder="Search notifications…"
              value={search} onChange={e=>setSearch(e.target.value)}
              style={{paddingLeft:36,minWidth:240}}/>
          </div>
          {unreadCount > 0 && (
            <button className="btn btn-secondary" onClick={handleMarkAllRead} disabled={markAllLoad}>
              {markAllLoad ? 'Marking…' : '✓ Mark All Read'}
            </button>
          )}
        </div>
      </div>

      <div className="stats-grid-4" style={{marginBottom:16}}>
        {[
          {label:'Total',    value:notifications.length,                                                icon:'🔔'},
          {label:'Unread',   value:unreadCount,                                                         icon:'📬'},
          {label:'Medicaid', value:notifications.filter(n=>n.NotificationType==='MedicaidAlert').length, icon:'🏥'},
          {label:'Training', value:notifications.filter(n=>n.NotificationType==='TrainingAlert').length, icon:'📚'},
        ].map(s=>(
          <div className="stat-card" key={s.label}>
            <div className="stat-card-header"><span className="stat-card-label">{s.label}</span><span style={{fontSize:20}}>{s.icon}</span></div>
            <div className="stat-card-value">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="notif-tab-row">
        {[{v:'all',l:'All'},{v:'unread',l:`Unread (${unreadCount})`},{v:'read',l:'Read'}].map(t=>(
          <button key={t.v} className={`notif-tab${tabFilt===t.v?' active':''}`} onClick={()=>setTabFilt(t.v)}>{t.l}</button>
        ))}
      </div>

      {loading ? (
        <div className="loading-state">⏳ Loading notifications…</div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🔔</div>
          <div className="empty-state-text">{search||tabFilt!=='all' ? 'No notifications match.' : 'No notifications yet.'}</div>
        </div>
      ) : (
        <div className="notif-list">
          {filtered.map(n => {
            const cfg = TYPE_CONFIG[n.NotificationType] || TYPE_CONFIG.default;
            return (
              <div key={n.ID} className={`notif-item${n.IsRead?' read':''}`}
                style={{borderLeft:`4px solid ${cfg.color}`, background: n.IsRead ? '#fff' : cfg.bg}}>
                <div className="notif-item-icon" style={{color:cfg.color}}>{cfg.icon}</div>
                <div className="notif-item-body">
                  <div className="notif-item-type" style={{color:cfg.color}}>{cfg.label}</div>
                  <div className="notif-item-msg">{n.Message}</div>
                  <div className="notif-item-time">{fmtDT(n.CreatedAt)}</div>
                </div>
                <div className="notif-item-actions">
                  {!n.IsRead && (
                    <button className="btn btn-sm btn-secondary" title="Mark as read"
                      onClick={()=>handleMarkRead(n.ID)}>✓ Read</button>
                  )}
                  <button className="btn btn-sm btn-danger" title="Delete"
                    onClick={()=>handleDelete(n.ID)}>🗑️</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}